import { Link } from "react-router-dom";
import { ArrowRight, Printer } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn } from "@/components/Reveal";

const questions = [
  "Are we having the same fight on repeat — and losing patience with slow progress?",
  "Is there a specific wound, like infidelity, that needs more than 50 minutes at a time?",
  "Are we at a crossroads and need clarity soon — not eventually?",
  "Do our schedules make weekly sessions genuinely unrealistic?",
  "Are we both willing to give one honest, focused day to this?",
];

const isList = [
  "Five private, structured hours — just the two of you and me",
  "Grounded in the Gottman Method and Emotionally Focused Therapy",
  "Real breaks, a clear arc, and a written plan to take home",
  "A strong start (or restart) when weekly pacing hasn't moved things",
];

const isNotList = [
  "A magic fix — one day opens the door, practice walks through it",
  "A group retreat or workshop — it is entirely private",
  "A replacement for weekly work when that is truly what's needed",
  "Right for an active crisis or an ongoing, undisclosed affair",
];

const IntensiveGuide = () => {
  usePageMeta(
    "Is an Intensive Right for Us? — A One-Page Guide | Holistic Healing Psychotherapy",
    "Five honest questions to help you decide whether a couples therapy intensive fits your relationship — and what an intensive is and isn't."
  );

  return (
    <div data-testid="intensive-guide-page">
      <section className="bg-cream-50 py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <p className="eyebrow mb-6" data-testid="guide-eyebrow">A One-Page Guide · From Breeza</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ink" data-testid="guide-headline">
              Is a Couples Therapy Intensive{" "}
              <em className="text-mauve-500">right for us?</em>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed" data-testid="guide-intro">
              Couples usually ask me this when weekly therapy feels too slow for where they are. This page is the
              honest answer I give them — five questions, and a clear picture of what an intensive is and isn't.
            </p>
            <button
              onClick={() => window.print()}
              data-testid="guide-print-button"
              className="no-print inline-flex items-center gap-2 mt-8 rounded-full border border-forest-800/20 text-forest-800 font-semibold text-sm px-6 py-3 transition-colors duration-300 hover:border-gold-500/60 hover:text-gold-700"
            >
              <Printer size={15} /> Save or print this page
            </button>
          </FadeIn>

          <div className="mt-14">
            <p className="eyebrow mb-6">The Five Questions</p>
            {questions.map((q, i) => (
              <FadeIn key={q} delay={i * 0.05}>
                <div className="flex gap-5 py-5 border-t border-forest-800/10" data-testid={`guide-question-${i + 1}`}>
                  <span className="mt-2.5 w-2.5 h-2.5 rotate-45 bg-gold-500/70 shrink-0" aria-hidden="true" />
                  <p className="font-serif text-xl sm:text-2xl text-forest-800 leading-snug">{q}</p>
                </div>
              </FadeIn>
            ))}
            <FadeIn delay={0.2}>
              <p className="mt-8 text-base text-ink/80 leading-relaxed border-l-2 border-gold-500/50 pl-6" data-testid="guide-reading-note">
                If you found yourself nodding to two or more, an intensive is worth a conversation. If there is an
                active crisis, or one of you has one foot out the door, a different pace may be kinder — either way,
                a free 20-minute consultation sorts it out gently.
              </p>
            </FadeIn>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 gap-6">
            <FadeIn>
              <div className="h-full rounded-2xl bg-forest-900 p-8" data-testid="guide-is-card">
                <p className="eyebrow mb-5">What an intensive is</p>
                <ul className="space-y-3 text-sm text-cream-50/80 leading-relaxed">
                  {isList.map((item) => (
                    <li key={item} className="flex gap-3"><span className="text-gold-400 shrink-0">—</span>{item}</li>
                  ))}
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="h-full rounded-2xl border border-forest-800/15 bg-cream-100 p-8" data-testid="guide-is-not-card">
                <p className="eyebrow mb-5">What an intensive isn't</p>
                <ul className="space-y-3 text-sm text-ink/75 leading-relaxed">
                  {isNotList.map((item) => (
                    <li key={item} className="flex gap-3"><span className="text-mauve-500 shrink-0">—</span>{item}</li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.15}>
            <div className="mt-14 text-center" data-testid="guide-cta-wrap">
              <p className="font-serif text-2xl sm:text-3xl text-forest-800 leading-snug max-w-xl mx-auto">
                Still unsure? That's exactly what the consultation is for.
              </p>
              <Link
                to="/contact"
                data-testid="guide-schedule-cta"
                className="inline-flex items-center gap-2 mt-7 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-700"
              >
                Schedule a Free Consultation <ArrowRight size={16} />
              </Link>
              <p className="mt-8 text-xs text-sage/70 max-w-md mx-auto" data-testid="guide-disclaimer">
                This guide is educational and not a substitute for therapy. If you are in crisis, call or text 988
                (Suicide & Crisis Lifeline) — support is available 24/7.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default IntensiveGuide;
